/**
 * Lógica de domínio pura (sem dependência de storage).
 * Calcula derivados por alimento, tendência honesta com poucos dados,
 * fila de re-exposição e a regra de origem.
 */
import {
  Alimento,
  DerivadosAlimento,
  GrupoAlimentar,
  Oferta,
  PassoEscada,
  StatusAlimento,
  Tendencia,
} from "./types";

const DIA_MS = 24 * 60 * 60 * 1000;

/** Nº mínimo de ofertas pra arriscar uma direção. Abaixo disso: "poucos_dados". */
export const MIN_OFERTAS_TENDENCIA = 3;

/**
 * Tendência honesta. Com poucos pontos, NÃO promete direção (o escopo é explícito:
 * "como representar tendência com poucos pontos sem enganar").
 * Compara a média dos passos da metade mais recente vs. a metade mais antiga.
 */
export function calcularTendencia(ofertas: Oferta[]): Tendencia {
  const ords = ofertas
    .slice()
    .sort((a, b) => a.data_hora.localeCompare(b.data_hora))
    .map((o) => o.passo_escada as number);

  if (ords.length < MIN_OFERTAS_TENDENCIA) return "poucos_dados";

  const meio = Math.floor(ords.length / 2);
  const antigos = ords.slice(0, meio);
  const recentes = ords.slice(ords.length - meio);
  const media = (xs: number[]) => xs.reduce((s, x) => s + x, 0) / xs.length;
  const delta = media(recentes) - media(antigos);

  if (delta >= 0.75) return "subindo";
  if (delta <= -0.75) return "caindo";
  return "estavel";
}

/** Deriva tudo que a UI precisa por alimento a partir das suas ofertas. */
export function calcularDerivados(
  alimentoId: string,
  ofertas: Oferta[],
  agora: Date = new Date(),
): DerivadosAlimento {
  const doAlimento = ofertas
    .filter((o) => o.alimento_id === alimentoId && o.confirmado)
    .sort((a, b) => a.data_hora.localeCompare(b.data_hora));

  const ultima = doAlimento[doAlimento.length - 1] ?? null;
  const passos = doAlimento.map((o) => o.passo_escada as number);

  return {
    alimento_id: alimentoId,
    n_ofertas: doAlimento.length,
    ultima_oferta: ultima?.data_hora ?? null,
    passo_atual: (ultima?.passo_escada as PassoEscada) ?? null,
    passo_maximo: passos.length ? (Math.max(...passos) as PassoEscada) : null,
    tendencia: calcularTendencia(doAlimento),
    dias_desde_ultima: ultima
      ? Math.floor((agora.getTime() - new Date(ultima.data_hora).getTime()) / DIA_MS)
      : null,
  };
}

/**
 * Fila de re-exposição (P0.6): alimentos `em_teste` não oferecidos há >= cadência.
 * Ordena pelos mais "esquecidos" primeiro.
 */
export function filaReexposicao(
  alimentos: Alimento[],
  ofertas: Oferta[],
  cadenciaDias: number,
  agora: Date = new Date(),
): Array<{ alimento: Alimento; dias_desde_ultima: number | null }> {
  return alimentos
    .filter((a) => a.status === "em_teste" && !a.arquivado)
    .map((a) => ({
      alimento: a,
      dias_desde_ultima: calcularDerivados(a.id, ofertas, agora).dias_desde_ultima,
    }))
    .filter((x) => x.dias_desde_ultima === null || x.dias_desde_ultima >= cadenciaDias)
    .sort((a, b) => (b.dias_desde_ultima ?? 1e9) - (a.dias_desde_ultima ?? 1e9));
}

/**
 * Sugestão de status derivado (P0.1 "marcar/derivar"). É só sugestão — o cuidador
 * pode sobrescrever. Nunca rebaixa um alimento já marcado `seguro` automaticamente.
 */
export function statusSugerido(
  atual: StatusAlimento,
  d: DerivadosAlimento,
): StatusAlimento {
  if (atual === "seguro") return "seguro";
  if (d.n_ofertas === 0) return "nao_oferecido";
  if ((d.passo_maximo ?? 0) >= PassoEscada.ComeUmPouco) return "seguro";
  return "em_teste";
}

/** Só pode entrar no modo escolha o que dá pra servir hoje (honrar a escolha — P0.3). */
export function elegiveisModoEscolha(alimentos: Alimento[]): Alimento[] {
  return alimentos.filter(
    (a) => !a.arquivado && (a.status === "seguro" || a.status === "em_teste"),
  );
}

/** Grupos que formam a base nutricional de uma refeição (proteína + carbo + legume). */
export const GRUPOS_BASE_REFEICAO: GrupoAlimentar[] = ["proteina", "carboidrato", "fibra"];

/**
 * Quanto a criança "já gosta" de um alimento, a partir dos dados gerados (testes,
 * progresso, tendência). Quanto maior, mais forte a recomendação pra base da refeição.
 * Seguro pesa muito; depois o maior passo já atingido; tendência de subida ajuda;
 * mais exposições desempatam levemente.
 */
export function scoreAceitacao(alimento: Alimento, ofertas: Oferta[]): number {
  const d = calcularDerivados(alimento.id, ofertas);
  const base = alimento.status === "seguro" ? 100 : 0;
  const tend = d.tendencia === "subindo" ? 10 : d.tendencia === "caindo" ? -5 : 0;
  return base + (d.passo_maximo ?? 0) * 8 + tend + d.n_ofertas * 0.5;
}

/**
 * Sugere a base da refeição de forma didática: os `porGrupo` alimentos mais aceitos
 * (pelo `scoreAceitacao`) de cada grupo base (proteína, carboidrato, legume). Só
 * considera elegíveis (seguro/em teste, não arquivados). Retorna os ids, na ordem
 * dos grupos. Padrão: 2 por grupo → 6 itens.
 */
export function sugerirBaseRefeicao(
  alimentos: Alimento[],
  ofertas: Oferta[],
  porGrupo = 2,
  grupos: GrupoAlimentar[] = GRUPOS_BASE_REFEICAO,
): string[] {
  // candidatos = tudo que dá pra montar no prato (não arquivado, não recusado),
  // inclusive "a experimentar" — o catálogo padrão vem assim. Rankeia pelo aceite.
  const candidatos = alimentos.filter((a) => !a.arquivado && a.status !== "recusado");
  const ids: string[] = [];
  for (const g of grupos) {
    const doGrupo = candidatos
      .filter((a) => a.grupo === g)
      .sort((x, y) => scoreAceitacao(y, ofertas) - scoreAceitacao(x, ofertas));
    ids.push(...doGrupo.slice(0, porGrupo).map((a) => a.id));
  }
  return ids;
}

/** Cria uma oferta a partir de uma escolha da criança: origem fixa + pendente. */
export function ofertaDaCrianca(
  base: Omit<Oferta, "origem" | "confirmado">,
): Oferta {
  return { ...base, origem: "crianca", confirmado: false };
}

/**
 * De que "fluxo" veio o registro, pra confrontar casa vs terapia na timeline.
 * Cuidador e criança são ambos "casa" (o que acontece no dia a dia da família);
 * o profissional é "terapia". É a única dimensão que o overlay precisa.
 */
export type FluxoOferta = "casa" | "terapia";

export function fluxoDaOferta(o: Oferta): FluxoOferta {
  return o.origem === "profissional" ? "terapia" : "casa";
}
